// @flow
import { connect } from 'react-redux'
import AudienceEdit from './AudienceEdit'
import * as actions from '../redux/ducks/copiedAdvertisementList/reducers'
import * as advertisementOperations from '../redux/ducks/advertisements/operations'
import type {
  Initial,
  State as AudienceState,
  ValidationErrors
} from '../redux/ducks/audienceEdit/reducers'
import * as audienceEditReducer from '../redux/ducks/audienceEdit/reducers'
import type { Store } from '../redux/ducks'
import _ from 'lodash'
import store from '../redux/store'
import type { RequestPayload as AdvertisementsRequestPayload } from './prepareProviderTemplateBlocks'
import type { Errors, Visibility } from './AudienceEdit'
import { Audience } from './constants'
import postJson from '../services/postJson'
import scrollToTop from '../shared/scrollToTop'
import { translateFunc, translations } from '../shared/localize'
import AdvertisementsKeeper from './advertisements.keeper'
import { getAudienceEditSegments } from '../redux/selectors/segmentation'
import { patch as segmentationPatch } from '../redux/ducks/segmentation/reducers'
import calculatePriceConstraints from './AudienceEdit/calculatePriceConstraints'
import { init as initLeadforms, parseResponse } from '../redux/ducks/leadforms/reducers'
import type {
  AdvertisementStatistics,
  OptionType,
  ProviderTemplateFieldsInfo,
  ProviderWorkPosition
} from './types'
import getJson from '../services/getJson'
import type { RawAdvertisement } from '../redux/ducks/AdvertisementDataAdapter'
import type { ErrorsPack } from './initErrorsFromResponse'
import { processAdvertisementErrors } from './initErrorsFromResponse'
import estimatesLoader from './AudienceEdit/Estimates/loader'
import EstimatesLoadingWatcher from '../services/callbacksDebouncer'
import { getDefaultFbPlacements } from './AudienceEdit/fbPlacements'
import { validVkEventGroups } from './AudienceEdit/VkEventGroups/VkEventGroupsContainer'
import biddingModeOptions from './AudienceEdit/biddingModeOptions'

type VkEventGroupRequestItem = {|
  id?: number,
  source_id: number | null,
  source_type: string,
  event_types: Array<number>
|}

type RequestDataItem = {|
  audience_type?: string,
  placement_type?: string,
  providers?: Array<string>,
  fbPlacements?: string,
  vk_public_id?: string,
  fb_public_id?: string,
  platform?: string,
  about_company: string,
  improve_advertisements: string,
  budget_portions?: Object,
  payment_type: string,
  birthday: string,
  relationship_status: string,
  included_groups: Array<Object>,
  excluded_groups: Array<Object>,
  vk_activity_groups: Array<Object>,
  included_vk_apps: string,
  excluded_vk_apps: string,
  budget_development: string,
  finish_date: string,
  name: string,
  age_limit: string,
  local_geo: Array<Object>,
  vk_micro_locations_type?: string,
  tm_micro_locations_visit_type?: string,
  tm_micro_locations_loc_type?: string,
  fb_micro_locations_type?: string,
  showtime: Array<Array<number>>,
  included_segment_ids: Array<number>,
  excluded_segment_ids: Array<number>,
  included_contact_ids: Array<number>,
  excluded_contact_ids: Array<number>,
  included_lookalike_segment_ids: Array<number>,
  included_lookalike_contact_ids: Array<number>,
  included_provider_targeting_source_ids: Array<number>,
  excluded_provider_targeting_source_ids: Array<number>,
  min_age?: string,
  max_age?: string,
  budget: string,
  budget_cycle: string,
  campaign_id?: string,
  sex: string,
  keywords: string,
  price: string,
  included_location_ids: Array<number>,
  excluded_location_ids: Array<number>,
  provider_demographic_ids: Array<number>,
  provider_interest_ids: Array<number>,
  fb_ad_interests: Array<Object>,
  fb_placements?: Object,
  vk_placement?: string,
  advertisements?: AdvertisementsRequestPayload,
  conversion_id?: ?number,
  pricelist_id?: number | '',
  leadform_id?: ?number,
  total_limit?: ?string,
  estimates?: ?Object,
  disclaimer_finance?: ?string,
  finance_name?: ?string,
  finance_license_number?: ?string,
  disclaimer_medical?: ?string,
  disclaimer_specialist?: ?string,
  disclaimer_supplements?: ?string,
  vk_event_groups?: ?Array<VkEventGroupRequestItem>,
  only_travellers?: ?string,
  vk_user_devices?: ?Array<number>,
  vk_positions?: ?Array<ProviderWorkPosition>,
  audience_name_template_id?: ?number,
  tm_bidding_mode?: ?string,
  fb_bidding_mode?: ?string,
  tm_shows_period?: ?string,
  tm_audience_shows_limit?: ?number,
  tm_ad_shows_limit?: ?number,
  vk_shows_limit?: ?number,
  imported?: ?boolean,
  vk_bidding_mode?: ?string,
  fb_positions?: ?Array<ProviderWorkPosition>
|}

type RequestData = {
  campaign?: {| name: string |},
  items: Array<RequestDataItem>,
  current_ad_account_id: number
}

type ErrorsResponseDataItem = $Shape<{
  audience_type: Errors,
  placement_type: Errors,
  providers: Errors,
  placements: Errors,
  vk_placement: Errors,
  vk_public_id: Errors,
  fb_public_id: Errors,
  platform: Errors,
  about_company: Errors,
  improve_advertisements: Errors,
  budget_portions: Errors,
  payment_type: Errors,
  birthday: Errors,
  relationship_status: Errors,
  included_groups: Errors,
  excluded_groups: Errors,
  vk_activity_groups: Errors,
  included_vk_apps: Errors,
  excluded_vk_apps: Errors,
  budget_development: Errors,
  finish_date: Errors,
  name: Errors,
  age_limit: Errors,
  local_geo: Errors,
  showtime: Errors,
  included_segment_ids: Errors,
  excluded_segment_ids: Errors,
  included_contact_ids: Errors,
  excluded_contact_ids: Errors,
  included_lookalike_segment_ids: Errors,
  included_lookalike_contact_ids: Errors,
  included_provider_targeting_source_ids: Errors,
  excluded_provider_targeting_source_ids: Errors,
  age: Errors,
  budget: Errors,
  budget_cycle: Errors,
  campaign_id: Errors,
  campaign_name: Errors,
  sex: Errors,
  keywords: Errors,
  price: Errors,
  included_location_ids: Errors,
  excluded_location_ids: Errors,
  provider_demographic_ids: Errors,
  provider_interest_ids: Errors,
  fb_ad_interests: Errors,
  fb_placements: Errors,
  advertisements: Array<{| items?: $Shape<ProviderTemplateFieldsInfo<any>> |}>,
  pricelist_id: Errors,
  conversion_id: Errors,
  leadform_id: Errors,
  total_limit: Errors,
  finance_name: Errors,
  finance_license_number: Errors,
  vk_event_groups: Errors
}>

type ErrorsResponseData = {|
  campaign?: {| name: Array<string> |},
  items: Array<ErrorsResponseDataItem>
|}

function getAdvertisementKeeper(state: Store): AdvertisementsKeeper {
  const audienceState = state.audienceEdit
  return new AdvertisementsKeeper({
    state,
    storeIds: state.copiedAdvertisementList.storeIds,
    providers: audienceState.providers,
    disabledStoreIds: state.copiedAdvertisementList.disabledStoreIds,
    audienceType: audienceState.audienceType,
    placementType: audienceState.placementTypes[0],
    vkPlacement: audienceState.vkPlacement
  })
}

function mapStateToProps(state: Store, ownProps: Object) {
  if (!state.audienceEdit.initial) return {}

  const initial: Initial = state.audienceEdit.initial
  const disabledStoreIds = state.copiedAdvertisementList.disabledStoreIds
  const audienceState = state.audienceEdit
  const action = ownProps.action
  const isNewAction = action === 'create'
  const isCopyAction = action === 'copy'
  const isEditAction = action === 'update'
  return {
    disabledStoreIds,
    ...state.audienceEdit,
    isNewAction,
    isCopyAction,
    isEditAction,
    isProductRetargeting: audienceState.audienceType === Audience.types.product_retargeting,
    isConversions: audienceState.audienceType === Audience.types.conversions,
    isLeadform: audienceState.audienceType === Audience.types.leadform,
    isSegmenting: getAudienceEditSegments(state).isSegmenting,
    // show price recommendation widget if default segmentation (looks like a crutch)
    showPriceRecommendationWidget:
      !getAudienceEditSegments(state).isSegmenting ||
      state.segmentation.segmentByProviders ||
      (state.segmentation.segmentByProviders && state.segmentation.segmentByImportantParams),
    visibility: calculateVisibility(state, initial, action),
    hasFormErrors: !_.every(_.pick(state.audienceEdit, audienceEditReducer.errorKeys), _.isEmpty),
    hasAdvertisementsErrors: getAdvertisementKeeper(state).hasErrors(),
    action,
    vkPlacementOptions: vkPlacementOptions(state.segmentation.segmentByProviders),
    showGeography:
      isNewAction ||
      isCopyAction ||
      (_.isEmpty(audienceState.localGeo) &&
        _.isEmpty(audienceState.includedLocationGroups) &&
        _.isEmpty(audienceState.excludedLocationIds))
  }
}

function vkPlacementOptions(segmentByProviders: boolean): Array<OptionType> {
  const t = translateFunc(translations.vk_placement_option)
  const options = [
    {
      id: Audience.vk_placements.promopost_with_button,
      name: t(Audience.vk_placements.promopost_with_button)
    },
    {
      id: Audience.vk_placements.promopost_without_button,
      name: t(Audience.vk_placements.promopost_without_button)
    },
    {
      id: Audience.vk_placements.promopost_without_public,
      name: t(Audience.vk_placements.promopost_without_public)
    }
  ]
  let withBigImageOption = {
    id: Audience.vk_placements.promopost_with_big_image,
    name: t(Audience.vk_placements.promopost_with_big_image)
  }

  return segmentByProviders ? [...options, withBigImageOption] : options
}

function calculateVisibility(
  state: Store,
  initial: Initial,
  action: 'create' | 'update' | 'copy'
): Visibility {
  const audienceState = state.audienceEdit
  const isNewAction = action === 'create'
  const isCopyAction = action === 'copy'
  const segmentingResult = getAudienceEditSegments(state)

  return {
    ...mergeVisibilities(
      _.map([audienceState, ...segmentingResult.items], (audienceStateItem) =>
        calculateVisibilityByAudienceState(audienceStateItem, isNewAction, isCopyAction)
      )
    ),
    advertisements: isCopyAction && !segmentingResult.isSegmenting,
    budgetPortions: initial.isAdmin
  }
}

function mergeVisibilities(visibilities: Array<Visibility>): Visibility {
  return _.reduce(visibilities, (result: Visibility, current: Visibility) => {
    return _.mergeWith(result, current, (a: boolean, b: boolean) => a || b)
  })
}

function calculateVisibilityByAudienceState(
  audienceState: AudienceState,
  isNewAction: boolean,
  isCopyAction: boolean
): Visibility {
  const isPostsPromotion = audienceState.audienceType === Audience.types.posts_promotion
  const isProductRetargeting = audienceState.audienceType === Audience.types.product_retargeting
  const isConversions = audienceState.audienceType === Audience.types.conversions
  const isPromopostWithoutPublic =
    audienceState.vkPlacement === Audience.vk_placements.promopost_without_public
  const isLeadform = audienceState.audienceType === Audience.types.leadform
  const isTargeting = audienceState.audienceType === Audience.types.targeting
  const isRetargeting = audienceState.audienceType === Audience.types.retargeting
  const isCarousel = audienceState.placementTypes[0] === Audience.placement_types.carousel
  const showVk = _.includes(audienceState.providers, 'vk')
  const showTm = _.includes(audienceState.providers, 'target_mail')
  const showFb = _.includes(audienceState.providers, 'facebook')

  return {
    audienceType: isNewAction || isCopyAction,
    placementType: (isNewAction || isCopyAction) && !isProductRetargeting && !isPostsPromotion,
    segmentation: isNewAction || isCopyAction,
    providers: isNewAction || isCopyAction,
    fbPlacements: showFb && (isNewAction || !_.isEmpty(audienceState.fbPlacements)),
    vkPlacement: showVk && !isPostsPromotion && !isLeadform && !isCarousel,
    vkPublic:
      !isLeadform &&
      !isPromopostWithoutPublic &&
      (_.includes(
        [
          Audience.placement_types.multi_format,
          Audience.placement_types.promo_post,
          Audience.placement_types.with_video,
          Audience.placement_types.carousel
        ],
        audienceState.placementTypes[0]
      ) ||
        isPostsPromotion) &&
      showVk,
    fbPublic: !isLeadform && showFb,
    campaign: isNewAction || isCopyAction,
    conversionId: isConversions,
    vk: showVk,
    tm: showTm,
    fb: showFb,
    gl: _.includes(audienceState.providers, 'google'),
    adAccountSelect: isCopyAction && !hideAdAccountSelect(audienceState),
    leadformId: isLeadform,
    vkEventGroups: showVk && (isTargeting || isRetargeting),
    vkBiddingModeSelect: showVk && !_.isEmpty(biddingModeOptions('vk', audienceState.paymentType)),
    tmBiddingModeSelect:
      showTm && !_.isEmpty(biddingModeOptions('target_mail', audienceState.paymentType)),
    fbBiddingModeSelect:
      showFb && !_.isEmpty(biddingModeOptions('facebook', audienceState.paymentType)),
    vkShowsLimitInfo: audienceState.paymentType === Audience.payment_types.cpm && showVk,
    tmShowsLimitInfo: audienceState.paymentType === Audience.payment_types.cpm && showTm,
    advertisements: false,
    budgetPortions: false
  }
}

function hideAdAccountSelect(audienceState: AudienceState): boolean {
  const {
    includedContactIds,
    includedSegmentIds,
    includedLookalikeSegmentIds,
    includedLookalikeContactIds,
    includedProviderTargetingSourceIds,
    excludedContactIds,
    excludedSegmentIds,
    excludedProviderTargetingSourceIds,
    pricelistId,
    adAccountOptions
  } = audienceState
  return (
    adAccountOptions.length < 2 ||
    _.some(
      [
        includedContactIds,
        includedSegmentIds,
        includedLookalikeContactIds,
        includedLookalikeSegmentIds,
        includedProviderTargetingSourceIds,
        excludedContactIds,
        excludedSegmentIds,
        excludedProviderTargetingSourceIds,
        pricelistId
      ],
      function(el) {
        return !_.isEmpty(el)
      }
    )
  )
}

function makeRequest(state: Store, dispatch: Function, action: 'create' | 'update' | 'copy') {
  const audienceState = state.audienceEdit
  const initial = audienceState.initial
  if (!initial) return

  const isNewAction = action === 'create'
  const isCopyAction = action === 'copy'
  const isEditAction = action === 'update'
  const isSegmenting = getAudienceEditSegments(state).isSegmenting

  const copyToAnotherAdAccount =
    isCopyAction && initial.currentAdAccountId !== audienceState.currentAdAccountId

  const requestData = data(initial, action)

  if (requestData.items.length === 0) return window.alert('Fix errors!')

  patch(dispatch, { isLoading: true })

  let submitPath = ''

  if (isNewAction || isCopyAction) {
    submitPath = '/api/v1/audiences'
  } else if (isEditAction) {
    submitPath = `/api/v1/audiences/${initial.id || 'undefinedID'}`
  }

  postJson(submitPath, {
    data: requestData
  })
    .then((response) => {
      if (response.success) {
        patch(dispatch, audienceEditReducer.initialErrors)
        let redirectTo = ''
        if (_.some(response.audiences, 'imported') || copyToAnotherAdAccount) {
          redirectTo = '/campaigns'
        } else if (isSegmenting) {
          const audienceIds: Array<number> = _.map(response.data, (item) => item.id)
          redirectTo = `/advertisement_templates/new/?audience_ids=${audienceIds.join(',')}`
        } else if (isNewAction) {
          redirectTo = `/audiences/${response.data[0].id}/advertisements/new`
        } else if (isCopyAction) {
          redirectTo = `/audiences/${response.data[0].id}/advertisements`
        } else {
          redirectTo = '/campaigns'
        }
        window.location = redirectTo
      } else {
        patch(dispatch, { isLoading: false })
        window.alert('Process error!')
      }
    })
    .fail((e: any) => {
      if (e.status === 422) {
        processErrors(e.responseJSON.message, dispatch)
        scrollToTop()
      } else {
        window.alert('Request error!')
      }
      patch(dispatch, { isLoading: false })
    })
}

function processErrors(errorResponse: ErrorsResponseData, dispatch: Function) {
  const itemErrors = aggregateValidationErrors(
    _.map(errorResponse.items, (item) => prepareAudienceErrors(item))
  )
  const result: ValidationErrors = {
    ...itemErrors,
    campaignNameErrors: (errorResponse.campaign || { name: [] }).name
  }
  patch(dispatch, result)
}

function aggregateValidationErrors(errorsCollection: Array<ValidationErrors>): ValidationErrors {
  return _.reduce(errorsCollection, (errorsLeft, errorsRight) =>
    _.mergeWith(errorsLeft, errorsRight, (leftValue, rightValue) =>
      _.isEmpty(leftValue) ? rightValue : leftValue
    )
  )
}

function prepareAudienceErrors(errors: ErrorsResponseDataItem): ValidationErrors {
  return {
    providersErrors: errors.providers || [],
    placementsErrors: errors.placements || [],
    vkPlacementErrors: errors.vk_placement || [],
    vkPublicErrors: errors.vk_public_id || [],
    fbPublicErrors: errors.fb_public_id || [],
    platformErrors: errors.platform || [],
    aboutCompanyErrors: errors.about_company || [],
    improveAdvertisementsErrors: errors.improve_advertisements || [],
    birthdayErrors: errors.birthday || [],
    relationshipStatusErrors: errors.relationship_status || [],
    includedGroupsErrors: errors.included_groups || [],
    excludedGroupsErrors: errors.excluded_groups || [],
    vkActivityGroupsErrors: errors.vk_activity_groups || [],
    includedVkAppsErrors: errors.included_vk_apps || [],
    excludedVkAppsErrors: errors.excluded_vk_apps || [],
    finishDateErrors: errors.finish_date || [],
    nameErrors: errors.name || [],
    ageLimitErrors: errors.age_limit || [],
    localGeoErrors: errors.local_geo || [],
    showtimeErrors: errors.showtime || [],
    includedSegmentIdsErrors: errors.included_segment_ids || [],
    excludedSegmentIdsErrors: errors.excluded_segment_ids || [],
    includedContactIdsErrors: errors.included_contact_ids || [],
    excludedContactIdsErrors: errors.excluded_contact_ids || [],
    includedLookalikeSegmentIdsErrors: errors.included_lookalike_segment_ids || [],
    includedLookalikeContactIdsErrors: errors.included_lookalike_contact_ids || [],
    includedProviderTargetingSourceIdsErrors: errors.included_provider_targeting_source_ids || [],
    excludedProviderTargetingSourceIdsErrors: errors.excluded_provider_targeting_source_ids || [],
    ageErrors: errors.age || [],
    budgetErrors: errors.budget || [],
    campaignErrors: errors.campaign_id || [],
    campaignNameErrors: [],
    keywordsErrors: errors.keywords || [],
    priceErrors: errors.price || [],
    includedLocationsErrors: errors.included_location_ids || [],
    excludedLocationsErrors: errors.excluded_location_ids || [],
    demographicErrors: errors.provider_demographic_ids || [],
    interestsErrors: errors.provider_interest_ids || [],
    advertisementsErrors: processAdvertisementsErrors(errors.advertisements),
    pricelistErrors: errors.pricelist_id || [],
    conversionIdErrors: errors.conversion_id || [],
    leadformIdErrors: errors.leadform_id || [],
    adAccountErrors: [],
    totalLimitErrors: errors.total_limit || [],
    financeNameErrors: errors.finance_name || [],
    financeLicenseNumberErrors: errors.finance_license_number || [],
    vkEventGroupsErrors: errors.vk_event_groups || []
  }
}

function processAdvertisementsErrors(responseErrors: Array<{| items?: Object |}>): Errors {
  const t = translateFunc(translations.audience_edit)
  const itemsErrors = (_.first(responseErrors) || {}).items || []
  const adaptedErrors: Array<ErrorsPack> = _.map(itemsErrors, (item) =>
    processAdvertisementErrors(item)
  )

  const state = store.getState()

  const activeStoreIds: any = _.difference(
    state.copiedAdvertisementList.storeIds,
    state.copiedAdvertisementList.disabledStoreIds
  )

  _.each(activeStoreIds, (storeId, index) => {
    const error = adaptedErrors[index]
    if (error) advertisementOperations.putErrors(storeId, error)
  })

  return _.isEmpty(responseErrors) ? [] : [t('invalid_advertisements')]
}

function data(initial: Initial, action: 'create' | 'update' | 'copy'): RequestData {
  const state: Store = store.getState()
  const audienceState = state.audienceEdit

  const visibility = calculateVisibility(state, initial, action)
  const advertisementsKeeper = getAdvertisementKeeper(state)

  let campaignData: Object = {}
  let itemsData = _.map(getAudienceEditSegments(state).items, (item) =>
    itemData(item, initial, visibility, advertisementsKeeper)
  )
  let statusData: Object = {}

  if (visibility.campaign) {
    if (audienceState.campaignId === 0) {
      campaignData = { campaign: { name: audienceState.campaignName } }
    }
  }

  if (action === 'create') statusData = { status: audienceState.status }

  return {
    ...campaignData,
    ...statusData,
    items: itemsData,
    current_ad_account_id: audienceState.currentAdAccountId
  }
}

function itemData(
  audienceState: AudienceState,
  initial: Initial,
  visibility: Visibility,
  advertisementsKeeper: AdvertisementsKeeper
) {
  const isMicrolocations = audienceState.geographyType === 'micro'
  const { paymentType } = audienceState
  const { priceConstraints } = initial
  const calculatedPriceConstraints = calculatePriceConstraints(
    priceConstraints[paymentType],
    audienceState.providers
  )
  const fixPrice = paymentType === Audience.payment_types.cpm
  let result: RequestDataItem = {
    platform: audienceState.platform,
    about_company: audienceState.aboutCompany,
    payment_type: audienceState.paymentType,
    birthday: audienceState.birthday || '',
    relationship_status: audienceState.relationshipStatus || '',
    included_groups: _.first(audienceState.includedGroups).value || [],
    excluded_groups: audienceState.excludedGroups || [],
    vk_activity_groups: _.first(audienceState.vkActivityGroups).value || [],
    included_vk_apps: _.first(audienceState.includedVkAppsGroups).value,
    excluded_vk_apps: audienceState.excludedVkApps,
    budget_development: audienceState.budgetDevelopment,
    finish_date: audienceState.finishDate || '',
    name: audienceState.name,
    age_limit: audienceState.ageLimit,
    local_geo: isMicrolocations ? audienceState.localGeo : [],
    showtime: audienceState.showtime,
    included_segment_ids: audienceState.includedSegmentIds,
    included_contact_ids: audienceState.includedContactIds,
    excluded_segment_ids: audienceState.excludedSegmentIds,
    excluded_contact_ids: audienceState.excludedContactIds,
    included_lookalike_segment_ids: audienceState.includedLookalikeSegmentIds,
    included_lookalike_contact_ids: audienceState.includedLookalikeContactIds,
    included_provider_targeting_source_ids: audienceState.includedProviderTargetingSourceIds,
    excluded_provider_targeting_source_ids: audienceState.excludedProviderTargetingSourceIds,
    budget: audienceState.budget,
    budget_cycle: audienceState.budgetCycle,
    sex: audienceState.sex || '',
    keywords: _.first(audienceState.keywordsGroups).value,
    price: fixPrice
      ? _.max([...calculatedPriceConstraints.minimum, parseFloat(audienceState.price)]).toString()
      : audienceState.price,
    included_location_ids: isMicrolocations
      ? []
      : _.first(audienceState.includedLocationGroups).value || [],
    excluded_location_ids: isMicrolocations ? [] : audienceState.excludedLocationIds || [],
    provider_demographic_ids: audienceState.demographicIds,
    provider_interest_ids: audienceState.interestIds,
    fb_ad_interests: _(audienceState.fbAdInterests)
      .map((interest) => _.pick(interest, ['id', 'label']))
      .value(),
    improve_advertisements: _.toNumber(audienceState.improveAdvertisements).toString(),
    pricelist_id: audienceState.pricelistId,
    leadform_id: audienceState.leadformId,
    total_limit: audienceState.totalLimit,
    audience_name_template_id: audienceState.selectedAudienceNameTemplateId,
    imported: audienceState.imported
  }
  if (audienceState.minAge) result.min_age = audienceState.minAge.toString()
  if (audienceState.maxAge) result.max_age = audienceState.maxAge.toString()

  result.audience_type = audienceState.audienceType
  result.placement_type = audienceState.placementTypes[0]

  if (visibility.providers) {
    result.providers = audienceState.providers
  }
  if (visibility.fbPlacements) {
    result.fb_placements = _.pickBy(
      (audienceState.fbPlacements: Object),
      (value) => !_.isEmpty(value)
    )
  }
  if (visibility.vkPlacement) {
    result.vk_placement = audienceState.vkPlacement
  }
  if (visibility.vkPublic) {
    result.vk_public_id = audienceState.vkPublic && audienceState.vkPublic.id
  }
  if (visibility.fbPublic) {
    result.fb_public_id = audienceState.fbPublic && audienceState.fbPublic.id
  }
  if (visibility.budgetPortions) {
    result.budget_portions = audienceState.budgetPortions
  }
  if (visibility.campaign) {
    if (audienceState.campaignId !== 0) {
      result.campaign_id = audienceState.campaignId.toString()
    }
  }
  if (visibility.conversionId) {
    result.conversion_id = audienceState.conversionId
  }
  if (visibility.vk) {
    result.disclaimer_finance = _.toNumber(audienceState.disclaimerFinance).toString()
    result.finance_name = audienceState.financeName
    result.finance_license_number = audienceState.financeLicenseNumber
    result.disclaimer_medical = _.toNumber(audienceState.disclaimerMedical).toString()
    result.disclaimer_specialist = _.toNumber(audienceState.disclaimerSpecialist).toString()
    result.disclaimer_supplements = _.toNumber(audienceState.disclaimerSupplements).toString()
    result.only_travellers = _.toNumber(audienceState.onlyTravellers).toString()
    result.vk_user_devices = audienceState.vkUserDevices
    result.vk_positions = audienceState.vkPositions
    result.fb_positions = audienceState.fbPositions
    if (visibility.vk && paymentType === Audience.payment_types.cpm)
      result.vk_shows_limit = audienceState.vkShowsLimit
  }
  if (visibility.vkEventGroups) {
    result.vk_event_groups = _.map(audienceState.vkEventGroups, (eventGroup) => {
      return {
        id: eventGroup.id,
        source_id: eventGroup.sourceId,
        source_type: eventGroup.sourceType,
        event_types: eventGroup.eventTypes
      }
    })
  }
  if (isMicrolocations) {
    result.vk_micro_locations_type = audienceState.vkLocalGeoType
    result.tm_micro_locations_visit_type = audienceState.tmLocalGeoVisitType
    result.tm_micro_locations_loc_type = audienceState.tmLocalGeoLocType
    result.fb_micro_locations_type = audienceState.fbLocalGeoType
  }
  if (visibility.vkBiddingModeSelect) result.vk_bidding_mode = audienceState.vkBiddingMode
  if (visibility.tmBiddingModeSelect) result.tm_bidding_mode = audienceState.tmBiddingMode
  if (visibility.fbBiddingModeSelect) result.fb_bidding_mode = audienceState.fbBiddingMode
  if (visibility.tm) {
    if (paymentType === Audience.payment_types.cpm) {
      result.tm_shows_period = audienceState.tmShowsPeriod
      result.tm_audience_shows_limit = audienceState.tmAudienceShowsLimit
      result.tm_ad_shows_limit = audienceState.tmAdShowsLimit
    }
  }
  result.advertisements = visibility.advertisements
    ? advertisementsKeeper.advertisementsToOutput()
    : []
  return result
}

const estimatesLoadingWatcher = new EstimatesLoadingWatcher(1500)

function mapDispatchToProps(dispatch: Function, ownProps: Object): any {
  const loadEstimates = () => {
    const { audienceEdit } = store.getState()
    patch(dispatch, { priceRecommendationsIsLoading: true })
    estimatesLoader(audienceEdit)
      .then((response) => {
        const priceRecommendations = _.first(response).estimates
        patch(dispatch, { priceRecommendations, priceRecommendationsIsLoading: false })
      })
      .catch(() => patch(dispatch, { priceRecommendationsIsLoading: false }))
  }
  const triggerEstimates = () => estimatesLoadingWatcher.debouncedOnChange()
  const changePaymentType = (paymentType) => {
    if (paymentType === Audience.payment_types.auto) {
      patch(dispatch, { tmBiddingMode: 'max_amount', fbBiddingMode: 'max_amount' })
    }
    patch(dispatch, { paymentType })
  }

  return {
    onInit: () => {
      estimatesLoadingWatcher.onChange(() => loadEstimates())

      const { action, audience_type: audienceType, campaign_id: campaignId, id } = ownProps
      let requestPromise = null
      if (action === 'create') {
        requestPromise = getJson('/audiences/new_data', {
          audience_type: audienceType,
          campaign_id: campaignId
        })
      } else if (action === 'update') {
        requestPromise = getJson(`/audiences/${id}/edit_data`)
      } else if (action === 'copy') {
        requestPromise = getJson(`/audiences/${id}/copy_data`)
      } else {
        throw new window.Error(`Unknow action ${action}`)
      }
      requestPromise.done((initial) => {
        dispatch(initLeadforms({ items: _.map(initial.leadforms, (item) => parseResponse(item)) }))
        dispatch(audienceEditReducer.init({ ...initial, action }))
        const visibility = calculateVisibility(store.getState(), initial, action)
        if (visibility.segmentation)
          dispatch(segmentationPatch({ segmentByProviders: true, segmentByImportantParams: true }))
        if (action === 'copy') {
          getJson(`/audiences/${id}/advertisements_data`).done(
            (response: {|
              advertisements: Array<RawAdvertisement>,
              statistics: { [number]: AdvertisementStatistics }
            |}) => {
              dispatch(actions.initAdvertisements(response.advertisements))
              patch(dispatch, { adStatistics: response.statistics })
            }
          )
        }
      })
    },
    onPersist: () => {
      const state: Store = store.getState()
      makeRequest(state, dispatch, ownProps.action)
    },
    onVkBiddingModeChange: (vkBiddingMode) => patch(dispatch, { vkBiddingMode }),
    onTmBiddingModeChange: (tmBiddingMode) => patch(dispatch, { tmBiddingMode }),
    onFbBiddingModeChange: (fbBiddingMode) => patch(dispatch, { fbBiddingMode }),
    onVkShowsLimitChange: (vkShowsLimit) => patch(dispatch, { vkShowsLimit }),
    onStatusChange: (status) => patch(dispatch, { status }),
    onIsAdminFieldShownChange: (isAdminFieldShown) => patch(dispatch, { isAdminFieldShown }),
    onBudgetDevelopmentChange: (budgetDevelopment) => patch(dispatch, { budgetDevelopment }),
    onShowtimeChange: (showtime) => patch(dispatch, { showtime }),
    onAgeLimitChange: (ageLimit) => patch(dispatch, { ageLimit }),
    onImproveAdvertisementsChange: (improveAdvertisements) =>
      patch(dispatch, { improveAdvertisements }),
    onBudgetPortionsChange: (budgetPortions) => patch(dispatch, { budgetPortions }),
    onConversionIdChange: (conversionId) => patch(dispatch, { conversionId }),
    onConversionsChange: (conversions) => patch(dispatch, { conversions }),
    onBirthdayChange: (birthday) => patch(dispatch, { birthday }),
    onRelationshipStatusChange: (relationshipStatus) => patch(dispatch, { relationshipStatus }),
    onLoadedIncludedGroupsChange: (loadedIncludedGroups) =>
      patch(dispatch, { loadedIncludedGroups }),
    onLoadedExcludedGroupsChange: (loadedExcludedGroups) =>
      patch(dispatch, { loadedExcludedGroups }),
    onIncludedVkAppsGroupsChange: (includedVkAppsGroups) => {
      patch(dispatch, { includedVkAppsGroups })
      triggerEstimates()
    },
    onExcludedVkAppsChange: (excludedVkApps) => {
      patch(dispatch, { excludedVkApps })
      triggerEstimates()
    },
    onLoadedVkActivityGroupsChange: (loadedVkActivityGroups) =>
      patch(dispatch, { loadedVkActivityGroups }),
    onLocalGeoDataChange: (diff) => {
      patch(dispatch, diff)
      triggerEstimates()
    },
    onPricelistIdChange: (pricelistId) => patch(dispatch, { pricelistId }),
    onOnlyTravellersChange: (onlyTravellers) => {
      patch(dispatch, { onlyTravellers })
      triggerEstimates()
    },
    onVkUserDevicesChange: (vkUserDevices) => {
      patch(dispatch, { vkUserDevices })
      triggerEstimates()
    },
    onVkPositionsChange: (vkPositions) => {
      patch(dispatch, { vkPositions })
      triggerEstimates()
    },
    onKeywordsGroupsChange: (keywordsGroups) => patch(dispatch, { keywordsGroups }),
    onVkPublicChange: (vkPublic) => patch(dispatch, { vkPublic }),
    onFbPublicChange: (fbPublic) => patch(dispatch, { fbPublic }),
    onIncludedGroupsChange: (includedGroups) => {
      patch(dispatch, { includedGroups })
      triggerEstimates()
    },
    onExcludedGroupsChange: (excludedGroups) => {
      patch(dispatch, { excludedGroups })
      triggerEstimates()
    },
    onVkActivityGroupsChange: (vkActivityGroups) => {
      patch(dispatch, { vkActivityGroups })
      triggerEstimates()
    },
    onPaymentTypeChange: changePaymentType,
    onInterestIdsChange: (interestIds) => {
      patch(dispatch, { interestIds })
      triggerEstimates()
    },
    onFbAdInterestsChange: (fbAdInterests) => {
      patch(dispatch, { fbAdInterests })
      triggerEstimates()
    },
    onDemographicIdsChange: (demographicIds) => {
      patch(dispatch, { demographicIds })
      triggerEstimates()
    },
    onLocalGeoChange: (localGeo) => {
      patch(dispatch, { localGeo })
      triggerEstimates()
    },
    onGeographyTypeChange: (geographyType) => {
      patch(dispatch, { geographyType })
      triggerEstimates()
    },
    onPlatformChange: (platform) => {
      const audienceState = store.getState().audienceEdit
      const { placementTypes, audienceType } = audienceState
      patch(dispatch, {
        platform,
        fbPlacements: getDefaultFbPlacements(placementTypes[0], audienceType, platform)
      })
      triggerEstimates()
    },
    onVkPlacementChange: (vkPlacement) => {
      const audienceState = store.getState().audienceEdit
      patch(dispatch, { vkPlacement })
      if (
        _.includes(
          [
            Audience.vk_placements.promopost_without_button,
            Audience.vk_placements.promopost_with_big_image
          ],
          vkPlacement
        )
      ) {
        changePaymentType(Audience.payment_types.cpm)
      } else {
        changePaymentType(Audience.payment_types.auto)
      }
      if (_.isEqual(Audience.vk_placements.promopost_without_public, vkPlacement)) {
        patch(dispatch, { vkEventGroups: validVkEventGroups(audienceState.vkEventGroups) })
      }
    },
    onSetCopyDefaultValues: () => {
      const audienceState = store.getState().audienceEdit
      if (audienceState.currentAdAccountId !== audienceState.initial.currentAdAccountId) {
        patch(dispatch, {
          campaignOptions: audienceState.initial.campaign.options,
          campaignId: 0,
          campaignName: '',
          currentAdAccountId: audienceState.initial.currentAdAccountId,
          vkPublic: {},
          fbPublic: {}
        })
      }
    },
    onLoadEstimates: loadEstimates,
    onTriggerEstimates: triggerEstimates,
    onFbPositionsChange: (fbPositions) => {
      patch(dispatch, { fbPositions })
      triggerEstimates()
    }
  }
}

export function patch(dispatch: Function, diff: $Shape<AudienceState>) {
  const segmentation = store.getState().segmentation
  dispatch(audienceEditReducer.patch(diff, segmentation))
}

const AudienceEditContainer = connect<Object, Object, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps
)(AudienceEdit)

export default AudienceEditContainer
